import { useState, useEffect, type ReactNode } from "react";
import { Building2, Mail, Loader2, ArrowLeft, PlusCircle, LogIn } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/stores/authStore";
import { useToast } from "@/hooks/use-toast";
import api, { getApiError } from "@/services/api";
import ConsentCheckboxes from "@/components/auth/ConsentCheckboxes";

type Mode = "loading" | "menu" | "create" | "join" | "done" | "pending" | "rejected";

interface LobbyStatus {
  state: "none" | "pending" | "rejected" | "active";
  email?: string | null;
  company_name?: string | null;
  company_code?: string | null;
  rejection_reason?: string | null;
  // 지금은 신청 가능하지만 직전 신청이 거부된 경우의 안내 (메뉴 화면에 표시)
  last_rejection_reason?: string | null;
  last_rejection_company?: string | null;
}

export default function NoCompanyPage() {
  const { logout, user } = useAuthStore();
  const { toast } = useToast();

  // 마운트 시 서버에서 실제 신청 상태를 확인하기 전까지는 로딩 상태로 시작한다.
  // (신청 후 새로고침하면 토큰은 그대로 no_company라, 로컬 state만으론 '승인 대기'를
  //  복원할 수 없어 항상 메뉴가 뜨던 문제를 방지)
  const [mode, setMode] = useState<Mode>("loading");
  const [appStatus, setAppStatus] = useState<LobbyStatus | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [doneMessage, setDoneMessage] = useState("");

  // 사용자 이메일: 제한 토큰 환경이라 user가 null일 수 있어 localStorage도 fallback
  const email = user?.email || "";

  // ---- 새 회사 만들기 폼 ----
  const [createForm, setCreateForm] = useState({
    company_name: "",
    company_code: "",
    business_number: "",
    company_address: "",
    company_phone: "",
    ceo_name: "",
    industry: "",
  });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);

  // ---- 기존 회사 가입 폼 ----
  const [joinCode, setJoinCode] = useState("");
  const [joinCompanyName, setJoinCompanyName] = useState<string | null>(null);
  const [joinChecking, setJoinChecking] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  // 마운트 시 실제 신청 상태 조회 → 승인 대기/거부면 해당 화면, 아니면 메뉴
  useEffect(() => {
    let cancelled = false;
    api
      .get<LobbyStatus>("/auth/lobby/status")
      .then((res) => {
        if (cancelled) return;
        setAppStatus(res.data);
        if (res.data.state === "pending") setMode("pending");
        else if (res.data.state === "rejected") setMode("rejected");
        else setMode("menu");
      })
      .catch(() => {
        // 조회 실패 시에도 로비 메뉴는 열어준다 (기존 동작 유지)
        if (!cancelled) setMode("menu");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 거부된 신청 → 계정을 대기(로비)로 되돌려 재신청 가능 상태로 만든다
  const handleReapply = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/lobby/reapply");
      const res = await api.get<LobbyStatus>("/auth/lobby/status");
      setAppStatus(res.data);
      setMode(res.data.state === "pending" ? "pending" : res.data.state === "rejected" ? "rejected" : "menu");
    } catch (error) {
      const apiError = getApiError(error);
      toast({ title: "재신청 준비 실패", description: apiError.message, variant: "destructive", duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  // 가입할 회사 코드 검증 (디바운스)
  useEffect(() => {
    const code = joinCode.trim();
    if (!code) {
      setJoinCompanyName(null);
      setJoinError(null);
      setJoinChecking(false);
      return;
    }
    let cancelled = false;
    setJoinChecking(true);
    setJoinError(null);
    const timer = setTimeout(() => {
      api
        .get("/auth/company-info", { params: { company_code: code } })
        .then((res) => {
          if (cancelled) return;
          setJoinCompanyName(res.data.name);
          setJoinError(null);
        })
        .catch(() => {
          if (cancelled) return;
          setJoinCompanyName(null);
          setJoinError("유효하지 않은 회사 코드입니다.");
        })
        .finally(() => {
          if (!cancelled) setJoinChecking(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [joinCode]);

  const handleCreate = async () => {
    if (!createForm.company_name.trim() || !createForm.company_code.trim() || !createForm.business_number.trim()) {
      toast({ title: "필수 항목을 입력해주세요", description: "기업명, 기업 코드, 사업자등록번호는 필수입니다.", variant: "destructive" });
      return;
    }
    const companyCode = createForm.company_code.trim().toLowerCase();
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(companyCode) || companyCode.includes("--")) {
      toast({
        title: "기업 코드를 확인해주세요",
        description: "기업 코드는 영문 소문자, 숫자, 하이픈(-)만 사용할 수 있고, 시작과 끝은 영문 또는 숫자여야 합니다. 예: mycompany",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    if (!agreeTerms || !agreePrivacy) {
      toast({ title: "약관 동의가 필요합니다", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/lobby/create-company", {
        ...createForm,
        company_code: companyCode,
        agree_to_terms: true,
        agree_to_privacy: true,
      });
      setDoneMessage(res.data?.message || "기업 등록 신청이 접수되었습니다. 슈퍼관리자 승인 후 로그인하실 수 있습니다.");
      setMode("done");
    } catch (error) {
      const apiError = getApiError(error);
      toast({ title: "기업 등록 실패", description: apiError.message, variant: "destructive", duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoin = async () => {
    if (!joinCompanyName) {
      toast({ title: "회사 코드를 확인해주세요", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/lobby/join-company", { company_code: joinCode.trim() });
      setDoneMessage(res.data?.message || "기업 가입 신청이 접수되었습니다. 관리자 승인 후 로그인하실 수 있습니다.");
      setMode("done");
    } catch (error) {
      const apiError = getApiError(error);
      toast({ title: "가입 신청 실패", description: apiError.message, variant: "destructive", duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const wrap = (children: ReactNode, opts?: { title: string; desc?: string }) => (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <picture className="mx-auto mb-4 block">
            <source srcSet="/icons/hc_02.svg" media="(prefers-color-scheme: dark)" />
            <img src="/icons/hc_01.svg" alt="HubCore" className="mx-auto h-10 w-auto" />
          </picture>
          <CardTitle className="text-2xl">{opts?.title ?? "회사 소속이 없습니다"}</CardTitle>
          {opts?.desc && <CardDescription className="text-base">{opts.desc}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-6">{children}</CardContent>
      </Card>
    </div>
  );

  // ---- 상태 확인 중 ----
  if (mode === "loading") {
    return wrap(
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>,
      { title: "상태 확인 중..." }
    );
  }

  // ---- 회사 승인 대기 (신청 완료 후 새로고침해도 유지) ----
  if (mode === "pending") {
    return wrap(
      <>
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-4 text-sm text-amber-900 dark:text-amber-100">
          <div className="mb-2 flex items-center gap-2 font-semibold">
            <Building2 className="h-5 w-5" />
            <span>{appStatus?.company_name || "신청한 회사"}</span>
          </div>
          기업 등록 신청이 접수되어 <b>슈퍼관리자 승인 대기 중</b>입니다.
          승인이 완료되면 이메일로 알림을 보내드리며, 다시 로그인하시면 이용하실 수 있습니다.
        </div>
        {appStatus?.email && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span>{appStatus.email}</span>
          </div>
        )}
        <Button className="w-full" onClick={() => logout()}>로그아웃</Button>
      </>,
      { title: "회사 승인 대기 중", desc: "관리자 승인을 기다리고 있습니다" }
    );
  }

  // ---- 신청 거부됨 ----
  if (mode === "rejected") {
    return wrap(
      <>
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <p className="font-semibold text-destructive">
            {appStatus?.company_name
              ? `${appStatus.company_name} 가입 신청이 거부되었습니다.`
              : "가입 신청이 거부되었습니다."}
          </p>
          {appStatus?.rejection_reason && (
            <p className="mt-1 text-muted-foreground">사유: {appStatus.rejection_reason}</p>
          )}
          <p className="mt-1 text-muted-foreground">
            다시 신청하거나 다른 회사에 가입할 수 있습니다.
          </p>
        </div>
        <div className="space-y-2">
          <Button className="w-full" size="lg" onClick={handleReapply} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 처리 중...
              </>
            ) : (
              "다시 신청하기"
            )}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => logout()}>
            로그아웃
          </Button>
        </div>
      </>,
      { title: "신청이 거부되었습니다" }
    );
  }

  // ---- 완료 화면 ----
  if (mode === "done") {
    return wrap(
      <>
        <div className="rounded-lg border bg-blue-50 dark:bg-blue-950/20 p-4 text-sm text-blue-900 dark:text-blue-100">
          {doneMessage}
        </div>
        <Button className="w-full" onClick={() => logout()}>로그아웃</Button>
        <p className="text-center text-sm text-muted-foreground">
          승인이 완료되면 다시 로그인해 주세요.
        </p>
      </>,
      { title: "신청이 접수되었습니다" }
    );
  }

  // ---- 새 회사 만들기 ----
  if (mode === "create") {
    return wrap(
      <>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="company_name">기업명 *</Label>
            <Input id="company_name" value={createForm.company_name}
              onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })} placeholder="(주)아이원" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="company_code">기업 코드 * <span className="text-xs text-muted-foreground">(영문 소문자·숫자·하이픈)</span></Label>
            <Input id="company_code" value={createForm.company_code} autoCapitalize="none" autoCorrect="off"
              onChange={(e) => setCreateForm({ ...createForm, company_code: e.target.value.toLowerCase() })} placeholder="mycompany" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="business_number">사업자등록번호 *</Label>
            <Input id="business_number" value={createForm.business_number}
              onChange={(e) => setCreateForm({ ...createForm, business_number: e.target.value })} placeholder="123-45-67890" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ceo_name">대표자명</Label>
            <Input id="ceo_name" value={createForm.ceo_name}
              onChange={(e) => setCreateForm({ ...createForm, ceo_name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="company_phone">기업 전화번호</Label>
            <Input id="company_phone" value={createForm.company_phone}
              onChange={(e) => setCreateForm({ ...createForm, company_phone: e.target.value })} placeholder="02-000-0000" />
          </div>
        </div>

        <ConsentCheckboxes
          termsAgreed={agreeTerms}
          privacyAgreed={agreePrivacy}
          onTermsChange={(c) => setAgreeTerms(c)}
          onPrivacyChange={(c) => setAgreePrivacy(c)}
        />

        <Button className="w-full" onClick={handleCreate} disabled={submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          기업 등록 신청
        </Button>
        <Button variant="ghost" className="w-full" onClick={() => setMode("menu")} disabled={submitting}>
          <ArrowLeft className="mr-2 h-4 w-4" /> 뒤로
        </Button>
      </>,
      { title: "새 회사 만들기", desc: "내가 관리자(대표)인 새 기업을 등록합니다." }
    );
  }

  // ---- 기존 회사 가입 ----
  if (mode === "join") {
    return wrap(
      <>
        <div className="space-y-1.5">
          <Label htmlFor="join_code">회사 코드</Label>
          <Input id="join_code" value={joinCode} autoCapitalize="none" autoCorrect="off"
            onChange={(e) => setJoinCode(e.target.value)} placeholder="회사로부터 받은 코드" />
          {joinChecking && <p className="text-sm text-muted-foreground">회사 확인 중...</p>}
          {joinError && <p className="text-sm text-destructive">{joinError}</p>}
          {joinCompanyName && !joinChecking && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-lg border bg-muted p-2">
              <Building2 className="h-5 w-5 text-primary" />
              <span className="font-semibold">{joinCompanyName}</span>
            </div>
          )}
        </div>

        <Button className="w-full" onClick={handleJoin} disabled={submitting || !joinCompanyName}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          가입 신청
        </Button>
        <Button variant="ghost" className="w-full" onClick={() => setMode("menu")} disabled={submitting}>
          <ArrowLeft className="mr-2 h-4 w-4" /> 뒤로
        </Button>
      </>,
      { title: "기존 회사에 가입", desc: "회사 코드로 가입 신청 후 관리자 승인을 받습니다." }
    );
  }

  // ---- 메뉴(기본) ----
  return wrap(
    <>
      {email && (
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span>{email}</span>
          </div>
        </div>
      )}

      {appStatus?.last_rejection_reason && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <p className="font-semibold text-destructive">
            {appStatus.last_rejection_company
              ? `직전 ${appStatus.last_rejection_company} 신청이 거부되었습니다.`
              : "직전 신청이 거부되었습니다."}
          </p>
          <p className="mt-1 text-muted-foreground">사유: {appStatus.last_rejection_reason}</p>
        </div>
      )}

      <p className="text-sm text-muted-foreground text-center">
        아직 소속된 회사가 없습니다. 새 회사를 만들거나 기존 회사에 가입 신청하세요.
      </p>

      <div className="space-y-2">
        <Button className="w-full" size="lg" onClick={() => setMode("create")}>
          <PlusCircle className="mr-2 h-5 w-5" /> 새 회사 만들기
        </Button>
        <Button variant="outline" className="w-full" size="lg" onClick={() => setMode("join")}>
          <LogIn className="mr-2 h-5 w-5" /> 기존 회사에 가입
        </Button>
        <Button variant="ghost" className="w-full" onClick={() => logout()}>
          로그아웃
        </Button>
      </div>
    </>,
    { title: "회사 소속이 없습니다", desc: "현재 어떤 회사에도 소속되어 있지 않습니다" }
  );
}
